import { useMemo, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SecurityIcon from "@mui/icons-material/Security";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import AppDialog from "../General/Dialog/AppDialog";
import LoadingState from "../General/LoadingState";

export default function UserPermissionsDialog({
  open,
  onClose,
  onSave,
  loading = false,
  saving = false,
  user = null,
  directPermissions = [],
  permissionsViaRole = [],
  availablePermissions = [],
  onRemoveDirectPermission,
  onAddDirectPermission,
}) {
  const [search, setSearch] = useState("");

  const normalizedSearch = search.trim().toLowerCase();

  const directPermissionNames = useMemo(
    () => new Set(directPermissions.map((permission) => permission.name)),
    [directPermissions],
  );

  const rolePermissionNames = useMemo(
    () => new Set(permissionsViaRole.map((permission) => permission.name)),
    [permissionsViaRole],
  );

  const filteredRolePermissions = useMemo(() => {
    return permissionsViaRole.filter((permission) =>
      permission.name.toLowerCase().includes(normalizedSearch),
    );
  }, [permissionsViaRole, normalizedSearch]);

  const filteredDirectPermissions = useMemo(() => {
    return directPermissions.filter((permission) =>
      permission.name.toLowerCase().includes(normalizedSearch),
    );
  }, [directPermissions, normalizedSearch]);

  const filteredAvailablePermissions = useMemo(() => {
    return availablePermissions.filter((permission) =>
      permission.name.toLowerCase().includes(normalizedSearch),
    );
  }, [availablePermissions, normalizedSearch]);

  const totalEffectivePermissions =
    rolePermissionNames.size +
    [...directPermissionNames].filter((name) => !rolePermissionNames.has(name))
      .length;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      title={
        user ? `Permisos del usuario - ${user.name}` : "Permisos del usuario"
      }
      subtitle="Administrá permisos directos sin modificar el rol asignado."
      icon={<SecurityIcon />}
      actions={
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "flex-end",
            gap: 1.2,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            endIcon={<CancelIcon />}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving || loading}
            endIcon={!saving ? <SendIcon /> : null}
          >
            {saving ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Guardar permisos"
            )}
          </Button>
        </Box>
      }
    >
      {loading ? (
        <LoadingState variant="inline" text="" />
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                p: 2,
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 2,
                bgcolor: "rgba(25,118,210,0.03)",
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                Información general
              </Typography>

              <Typography variant="body2" sx={{ mb: 0.7 }}>
                <strong>Usuario:</strong> {user?.name || "-"}
              </Typography>

              <Typography variant="body2" sx={{ mb: 0.7 }}>
                <strong>Rol actual:</strong> {user?.role || "-"}
              </Typography>

              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Chip
                  label={`Por rol: ${permissionsViaRole.length}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Directos: ${directPermissions.length}`}
                  color="info"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Totales efectivos: ${totalEffectivePermissions}`}
                  color="success"
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  color: "text.secondary",
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
                <Typography variant="body2">
                  Los permisos heredados por rol se muestran solo como
                  referencia. Desde acá solo podés agregar o quitar permisos
                  directos del usuario.
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Buscar permiso"
              placeholder="Filtrar por nombre"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                p: 2,
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 2,
                height: "100%",
                minHeight: 360,
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 1 }}>
                Permisos heredados por rol
              </Typography>

              <Divider sx={{ mb: 1.5 }} />

              <Box
                sx={{
                  maxHeight: 340,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                {filteredRolePermissions.length > 0 ? (
                  filteredRolePermissions.map((permission) => (
                    <Box
                      key={`role-${permission.id}`}
                      sx={{
                        px: 1.5,
                        py: 1.2,
                        borderRadius: 1.5,
                        bgcolor: "rgba(25,118,210,0.06)",
                        border: "1px solid rgba(25,118,210,0.12)",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {permission.name}
                      </Typography>

                      <Chip
                        label="Heredado por rol"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay permisos heredados para mostrar.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                p: 2,
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 2,
                height: "100%",
                minHeight: 360,
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 1 }}>
                Permisos directos asignados
              </Typography>

              <Divider sx={{ mb: 1.5 }} />

              <Box
                sx={{
                  maxHeight: 340,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                {filteredDirectPermissions.length > 0 ? (
                  filteredDirectPermissions.map((permission) => (
                    <Box
                      key={`direct-${permission.id}`}
                      sx={{
                        px: 1.5,
                        py: 1.2,
                        borderRadius: 1.5,
                        bgcolor: "rgba(2,136,209,0.06)",
                        border: "1px solid rgba(2,136,209,0.12)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, wordBreak: "break-word" }}
                        >
                          {permission.name}
                        </Typography>

                        <Chip
                          label="Directo"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      </Box>

                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={<RemoveCircleOutlineIcon />}
                        onClick={() => onRemoveDirectPermission(permission)}
                      >
                        Quitar
                      </Button>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    El usuario no tiene permisos directos asignados.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                p: 2,
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 2,
                height: "100%",
                minHeight: 360,
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 1 }}>
                Permisos disponibles para agregar
              </Typography>

              <Divider sx={{ mb: 1.5 }} />

              <Box
                sx={{
                  maxHeight: 340,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                {filteredAvailablePermissions.length > 0 ? (
                  filteredAvailablePermissions.map((permission) => (
                    <Box
                      key={`available-${permission.id}`}
                      sx={{
                        px: 1.5,
                        py: 1.2,
                        borderRadius: 1.5,
                        bgcolor: "rgba(76,175,80,0.06)",
                        border: "1px solid rgba(76,175,80,0.12)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, wordBreak: "break-word" }}
                        >
                          {permission.name}
                        </Typography>

                        <Chip
                          label="Disponible"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      </Box>

                      <Button
                        size="small"
                        color="success"
                        variant="text"
                        startIcon={<AddIcon />}
                        onClick={() => onAddDirectPermission(permission)}
                      >
                        Agregar
                      </Button>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay más permisos disponibles para agregar.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}
    </AppDialog>
  );
}
